<!--
  SOF detail: two-step pick → stage flow (both mobile and desktop).
  Confirmation panel replaces the whole detail after a successful complete.
-->
<div class="sof-detail" [class.sof-detail-mobile]="!isDesktop">
  <!-- ═══ Confirmation panel (after Mark ready for delivery) ═══ -->
  <ng-container *ngIf="state.completionSummary | async as summary; else detail">
    <div class="sof-confirm">
      <div class="sof-confirm-hero">
        <div class="sof-confirm-check">
          <ion-icon name="checkmark-outline"></ion-icon>
        </div>
        <div>
          <div class="sof-confirm-title">
            {{
              summary.sofComplete
                ? 'SOF complete — ready for delivery'
                : summary.completedPos.length +
                  ' PO' +
                  (summary.completedPos.length === 1 ? '' : 's') +
                  ' ready for delivery'
            }}
          </div>
          <div class="sof-confirm-subtitle">
            {{
              summary.sofComplete
                ? 'Every PO on SOF ' + summary.sof + ' is received. Delivery team can now load it.'
                : 'SOF ' + summary.sof + ' still has other POs to receive before it is fully done.'
            }}
          </div>
        </div>
      </div>

      <div class="sof-confirm-summary">
        <div class="sof-confirm-row">
          <div class="sof-confirm-label">Order</div>
          <div class="sof-confirm-value">
            SOF {{ summary.sof }}
            <ng-container *ngIf="summary.orderTo"> · {{ summary.orderTo }}</ng-container>
          </div>
        </div>
        <div class="sof-confirm-row">
          <div class="sof-confirm-label">POs staged</div>
          <div class="sof-confirm-value">{{ summary.completedPos.join(', ') }}</div>
        </div>
        <div class="sof-confirm-row">
          <div class="sof-confirm-label">Staged at</div>
          <div class="sof-confirm-value sof-confirm-highlight">
            {{ summary.stagedAt }}
          </div>
        </div>
        <div class="sof-confirm-row">
          <div class="sof-confirm-label">Items staged</div>
          <div class="sof-confirm-value">
            {{ summary.itemCount }} item{{ summary.itemCount === 1 ? '' : 's' }}
          </div>
        </div>
        <div class="sof-confirm-row">
          <div class="sof-confirm-label">Staged by</div>
          <div class="sof-confirm-value">{{ summary.stagedBy }}</div>
        </div>
      </div>

      <ion-button
        expand="block"
        color="primary"
        class="sof-confirm-button"
        (click)="finishCompletion()"
      >
        Back to order queue
      </ion-button>
    </div>
  </ng-container>

  <!-- ═══ Detail ═══ -->
  <ng-template #detail>
    <!-- Top nav: back to queue (pick) or back to pick (stage) -->
    <button
      type="button"
      class="sof-back-button"
      *ngIf="!isDesktop && step === 'pick'"
      (click)="backToQueue.emit()"
    >
      <ion-icon name="chevron-back-outline"></ion-icon>
      Back to queue
    </button>

    <div class="sof-detail-loading" *ngIf="state.detailLoading | async">
      <ion-spinner name="dots"></ion-spinner>
      <span>Loading SOF…</span>
    </div>

    <ng-container *ngIf="state.selectedOrder | async as order">
      <!-- Header meta card (pick step only) -->
      <div class="sof-meta-card" *ngIf="step === 'pick'">
        <div class="sof-meta-title-row">
          <span class="sof-meta-sof">SOF {{ order.sof }}</span>
          <span class="sof-pill sof-pill-hold" *ngIf="onHold">ON HOLD</span>
          <span
            class="sof-status-chip"
            [ngClass]="
              'sof-status-chip-' +
              chipFor(
                (state.sofDetails | async)?.receivingStatus ?? order.receivingStatus
              ).color
            "
          >
            <span class="sof-status-dot"></span>
            {{
              chipFor(
                (state.sofDetails | async)?.receivingStatus ?? order.receivingStatus
              ).label
            }}
          </span>
        </div>
        <div class="sof-meta-order-to">{{ order.orderTo }}</div>
        <div class="sof-meta-grid" *ngIf="state.sofDetails | async as details">
          <span>
            <strong>Contact:</strong> {{ details.contactName }}
            <ng-container *ngIf="details.contactPhone">
              · {{ details.contactPhone }}
            </ng-container>
          </span>
          <span>
            <strong>Expected delivery:</strong>
            {{ details.deliveryDate | date : 'MM/dd/yyyy' }}
          </span>
        </div>
      </div>

      <!-- General notes banner (pick step only) -->
      <div
        class="advisory advisory-info advisory-solid sof-warning-banner"
        *ngIf="step === 'pick' && (state.sofDetails | async)?.generalNotes as generalNotes"
      >
        <ion-icon name="warning"></ion-icon>
        <div>
          <div class="sof-warning-banner-title">General note</div>
          <div class="sof-warning-banner-text">{{ generalNotes }}</div>
        </div>
      </div>

      <!-- Shared items-loading spinner -->
      <div class="sof-items-loading" *ngIf="state.itemsLoading | async">
        <ion-spinner name="dots"></ion-spinner>
        <span>Loading items…</span>
      </div>

      <!-- ══════════ STEP 1: PICK (all POs, cross-PO checking) ══════════ -->
      <ng-container *ngIf="step === 'pick' && !(state.itemsLoading | async)">
        <div
          class="sof-po-card"
          [class.sof-po-card-complete]="poStatusChip(po).color === 'success'"
          *ngFor="let po of state.pos | async; trackBy: trackByPo"
        >
          <div class="sof-po-header">
            <div class="sof-po-header-main">
              <div class="sof-po-number">PO {{ po.poNumber }}</div>
              <span
                class="sof-po-status"
                [ngClass]="'sof-po-status-' + poStatusChip(po).color"
              >
                <span class="sof-po-status-dot"></span>
                {{ poStatusChip(po).label }}
              </span>
            </div>
            <!-- Non-partial only: on a partial PO the READ BEFORE PULLING banner
                 directly below says the same thing, louder. -->
            <span class="sof-pill sof-pill-entire" *ngIf="!isPartial(po)">
              {{ po.orderType || 'Entire order' }}
            </span>
            <ion-button
              fill="clear"
              size="small"
              class="sof-po-report-button"
              title="PO report"
              (click)="openPoReport(po, $event)"
            >
              <ion-icon slot="icon-only" name="document-text-outline"></ion-icon>
            </ion-button>
          </div>

          <!-- Foreman's partial note -->
          <div class="advisory advisory-info advisory-solid sof-warning-banner sof-po-partial-banner" *ngIf="isPartial(po)">
            <ion-icon name="warning"></ion-icon>
            <div>
              <div class="sof-warning-banner-title">Read before pulling</div>
              <div class="sof-warning-banner-text">"{{ po.partialNotes }}"</div>
            </div>
          </div>

          <div class="sof-items">
            <div class="sof-items-empty" *ngIf="itemsForPo(po).length === 0">
              No items found for this PO.
            </div>

            <ng-container *ngIf="itemsForPo(po).length > 0">
              <!-- Per-PO select all/none -->
              <div class="sof-items-header" *ngIf="!readOnly">
                <label
                  class="sof-select-all"
                  [class.sof-select-all-disabled]="!hasToggleableItems(po)"
                >
                  <input
                    type="checkbox"
                    [checked]="allCheckedForPo(po)"
                    [disabled]="!hasToggleableItems(po)"
                    (change)="toggleSelectAllForPo(po)"
                  />
                  <span>{{ allCheckedForPo(po) ? 'Unselect all' : 'Select all' }}</span>
                </label>
                <span class="sof-items-count">
                  {{ itemsForPo(po).length }} item{{ itemsForPo(po).length === 1 ? '' : 's' }}
                </span>
              </div>

              <!-- Desktop: table -->
              <div class="sof-items-table-wrapper" *ngIf="isDesktop">
                <table class="sof-items-table">
                  <thead>
                    <tr>
                      <th class="sof-col-check"></th>
                      <th>Items</th>
                      <th>Qty</th>
                      <th>Location</th>
                      <th>Date In</th>
                      <th class="sof-col-notes">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      *ngFor="let item of itemsForPo(po); trackBy: trackByItemId"
                      [class.sof-row-checked]="item.checkedOut"
                      [class.sof-row-pulled]="item.alreadyPulled"
                    >
                      <td class="sof-col-check">
                        <input
                          type="checkbox"
                          [checked]="item.checkedOut"
                          [disabled]="readOnly || item.alreadyPulled"
                          (change)="toggleItem(item)"
                        />
                      </td>
                      <td>
                        {{ item.itemDescription }}
                        <span class="sof-pulled-tag" *ngIf="item.alreadyPulled">Pulled</span>
                      </td>
                      <td><span class="sof-qty-chip">{{ item.quantity }}</span></td>
                      <td>
                        <span class="sof-loc-chip" *ngFor="let loc of locationChips(item)">
                      {{ loc }}
                        </span>
                      </td>
                      <td>{{ item.dateIn | date : 'MM/dd/yyyy' }}</td>
                      <td class="sof-col-notes">
                        <ion-textarea
                          *ngIf="!readOnly"
                          [value]="item.notes"
                          [autoGrow]="true"
                          rows="1"
                          placeholder="Notes"
                          (ionInput)="onNotesChange(item, $event)"
                        ></ion-textarea>
                        <span *ngIf="readOnly">{{ item.notes }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile: card rows -->
              <div class="sof-item-rows" *ngIf="!isDesktop">
                <div
                  class="sof-item-row"
                  *ngFor="let item of itemsForPo(po); trackBy: trackByItemId"
                  [class.sof-item-row-checked]="item.checkedOut"
                  [class.sof-item-row-pulled]="item.alreadyPulled"
                >
                  <button
                    type="button"
                    class="sof-item-row-main"
                    [disabled]="readOnly || item.alreadyPulled"
                    (click)="toggleItem(item)"
                  >
                    <span
                      class="sof-item-checkbox"
                      [class.sof-item-checkbox-checked]="item.checkedOut"
                    >
                      <ion-icon name="checkmark-outline" *ngIf="item.checkedOut"></ion-icon>
                    </span>
                    <span class="sof-item-body">
                      <span class="sof-item-chips">
                        <span
                          class="sof-loc-chip"
                          [class.sof-loc-chip-checked]="item.checkedOut"
                          *ngFor="let loc of locationChips(item)"
                        >
                          <ion-icon name="navigate-outline"></ion-icon>{{ loc }}
                        </span>
                        <span class="sof-qty-chip" *ngIf="item.quantity">{{ item.quantity }}</span>
                      </span>
                      <span class="sof-item-description">
                        {{ item.itemDescription }}
                        <span class="sof-pulled-tag" *ngIf="item.alreadyPulled">Pulled</span>
                      </span>
                      <span class="sof-item-received" *ngIf="item.dateIn">
                        Received {{ item.dateIn | date : 'MM/dd/yyyy' }}
                      </span>
                      <span class="sof-item-notes-preview" *ngIf="item.notes">
                        <ion-icon name="document-text-outline"></ion-icon>{{ item.notes }}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="sof-item-notes-toggle"
                    *ngIf="!readOnly"
                    (click)="toggleNotesEditor(item, $event)"
                  >
                    <ion-icon name="create-outline"></ion-icon>
                    {{ openNotesItemId === item.id ? 'Close notes' : 'Add/Edit notes' }}
                  </button>
                  <div
                    class="sof-item-notes-editor"
                    *ngIf="!readOnly && openNotesItemId === item.id"
                  >
                    <ion-textarea
                      [value]="item.notes"
                      [autoGrow]="true"
                      rows="2"
                      placeholder="Notes for this item"
                      (ionInput)="onNotesChange(item, $event)"
                    ></ion-textarea>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Proof photos (read-only history: view only) -->
        <div class="sof-stage-photos" *ngIf="readOnly">
          <div class="sof-stage-where-label">Proof photos</div>
          <div class="sof-items-loading" *ngIf="state.photosLoading | async">
            <ion-spinner name="dots"></ion-spinner>
            <span>Loading photos…</span>
          </div>
          <ng-container *ngIf="state.photos | async as photos">
            <app-photo-grid-viewer
              *ngIf="photos.length > 0"
              [photos]="photos"
              [viewingLogRow]="true"
              [photosEditable]="false"
            ></app-photo-grid-viewer>
            <div
              class="sof-items-empty"
              *ngIf="photos.length === 0 && !(state.photosLoading | async)"
            >
              No proof photos for this SOF.
            </div>
          </ng-container>
        </div>

        <!-- Pick-step actions (editable only): save, or proceed to stage.
             Save lives on THIS step as well as the stage step because per-item
             notes are only editable here: with Save on the stage step alone,
             and Next disabled until something is checked, a visit that edited
             notes but pulled nothing had no way to save at all and lost the
             edits on navigate. Mark ready stays on the stage step, where the
             staging area it requires is entered. -->
        <div
          class="sof-actions"
          [class.sof-actions-bottom-bar]="!isDesktop"
          *ngIf="!readOnly"
        >
          <div class="sof-actions-scope">
            {{ state.checkedCount }} of {{ state.totalCount }} item{{ state.totalCount === 1 ? '' : 's' }} pulled
            across this SOF's POs. Save keeps your checks and notes without
            finishing.
          </div>
          <div class="sof-actions-buttons">
            <ion-button
              color="medium"
              fill="outline"
              [disabled]="state.saving | async"
              (click)="save()"
            >
              <ion-spinner name="dots" *ngIf="state.saving | async" slot="start"></ion-spinner>
              Save progress
            </ion-button>
            <ion-button
              color="primary"
              [disabled]="state.checkedCount === 0"
              (click)="goToStage()"
            >
              Next: Stage {{ state.checkedCount }}
            </ion-button>
          </div>
        </div>
      </ng-container>

      <!-- ══════════ STEP 2: STAGE ══════════ -->
      <ng-container *ngIf="step === 'stage' && !readOnly">
        <!-- Stage header (replaces the SOF meta card on this step) -->
        <div class="sof-stage-header">
          <button type="button" class="sof-stage-back" (click)="backToPick()">
            <ion-icon name="chevron-back-outline"></ion-icon>
          </button>
          <div class="sof-stage-header-titles">
            <div class="sof-stage-title">Stage order</div>
            <div class="sof-stage-subtitle">SOF {{ order.sof }}</div>
          </div>
        </div>

        <div class="sof-stage">
          <div class="sof-stage-heading">
            Staging {{ state.checkedCount }} pulled item{{ state.checkedCount === 1 ? '' : 's' }}
          </div>

          <!-- Condensed flat list of everything being pulled -->
          <div class="sof-stage-list">
            <div class="sof-stage-row" *ngFor="let item of state.checkedItems; trackBy: trackByItemId">
              <ion-icon class="sof-stage-check" name="checkmark-outline" color="success"></ion-icon>
              <div class="sof-stage-row-body">
                <div class="sof-stage-row-po">
                  PO {{ item.poNumber }}
                  <span *ngIf="item.quantity">· {{ item.quantity }}</span>
                </div>
                <div class="sof-stage-row-desc">{{ item.itemDescription }}</div>
              </div>
              <div class="sof-stage-row-loc" *ngIf="firstLocation(item)">
                from {{ firstLocation(item) }}
              </div>
            </div>
          </div>

          <!-- Where staged: manual entry only (whole SOF) -->
          <div class="sof-stage-where">
            <div class="sof-stage-where-label">Where did you stage it?</div>
            <ion-input
              class="sof-staging-input"
              placeholder="Dock, lane A, floor, etc"
              [value]="state.stagingArea"
              (ionInput)="onStagingAreaChange($event)"
            ></ion-input>
            <div class="sof-actions-scope">
              One staging spot applies to the whole SOF.
            </div>
          </div>

          <!-- Proof photos: captured here, uploaded with Save progress / Mark ready -->
          <div class="sof-stage-photos">
            <div class="sof-stage-where-label">Proof photos</div>
            <app-photo-uploader
              [emitOnly]="true"
              [isMobile]="!isDesktop"
              [allowLibrary]="false"
              (photosPicked)="onPhotosPicked($event)"
            ></app-photo-uploader>

            <ng-container *ngIf="state.newPhotos | async as newPhotos">
              <div class="sof-photos-group" *ngIf="newPhotos.length > 0">
                <div class="sof-photos-subheading">
                  {{ newPhotos.length }} new photo{{ newPhotos.length === 1 ? '' : 's' }}
                  — uploads when you save
                </div>
                <app-photo-grid-viewer
                  [photos]="newPhotos"
                  [viewingLogRow]="true"
                  badgeText="Not saved yet"
                  badgeColor="warning"
                  (deletePhoto)="deleteNewPhoto($event)"
                  (deleteAllPhotos)="deleteAllNewPhotos()"
                ></app-photo-grid-viewer>
              </div>
            </ng-container>

            <ng-container *ngIf="state.photos | async as photos">
              <div class="sof-photos-group" *ngIf="photos.length > 0">
                <div class="sof-photos-subheading">Uploaded photos</div>
                <app-photo-grid-viewer
                  [photos]="photos"
                  [viewingLogRow]="true"
                  [photosEditable]="canDeletePhotos"
                  badgeText="Saved"
                  badgeColor="success"
                  (deletePhoto)="deleteExistingPhoto($event)"
                  (deleteAllPhotos)="deleteAllExistingPhotos()"
                ></app-photo-grid-viewer>
              </div>
            </ng-container>

            <div class="sof-items-loading" *ngIf="state.photosLoading | async">
              <ion-spinner name="dots"></ion-spinner>
              <span>Working on photos…</span>
            </div>
          </div>
        </div>

        <!-- Stage-step actions (Save progress + Mark ready; back lives in the header) -->
        <div class="sof-actions" [class.sof-actions-bottom-bar]="!isDesktop">
          <div class="sof-actions-scope">
            Save progress keeps this In Progress; Mark ready hands it to delivery.
            Both save where you staged it.
          </div>
          <div class="sof-actions-buttons">
            <ion-button
              color="medium"
              fill="outline"
              [disabled]="state.saving | async"
              (click)="save()"
            >
              <ion-spinner name="dots" *ngIf="state.saving | async" slot="start"></ion-spinner>
              Save progress
            </ion-button>
            <ion-button
              color="success"
              [disabled]="(state.saving | async) || !canMarkComplete"
              (click)="markComplete()"
            >
              Mark ready for delivery
            </ion-button>
          </div>
        </div>
      </ng-container>
    </ng-container>
  </ng-template>
</div>
